import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { CloseIconDark, IdHelpDesktop } from 'src/assets/images';
import useImageUpload from 'src/features/onboarding//hooks/use-image-upload';
import { ImageUploadActionType } from 'src/features/onboarding//types/image-upload-reducer-actions';
import { ImageUploadJourney } from 'src/features/onboarding//context/image-upload-context';
import WebcamChecker from 'src/features/common/components/revamp-webcam-checker/revamp-webcam-checker';
import { ApplicationContext } from '../../context';
import { Environment } from 'src/Environment';
import {
  getProductName,
  getProductNamesInCart,
} from 'src/features/dpo/helpers/tealium-service-helpers';

export function TakePhotoTipsModal() {
  const { imageUploadState, imageUploadDispatch } = useImageUpload();
  const { application, catalogue, onboardingTealiumService } = useContext(ApplicationContext);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const tips = [
    'Position the details page in the block as shown in the picture',
    'Make sure the top of the page is on the right hand side',
    'Check that the photo is clear and readable',
    'Avoid flash and reflective light',
  ];

  const productNames = useMemo(() => {
    if (!Environment.current.disabledFeatures.dpo) {
      return getProductNamesInCart(application.cart || []);
    }
    return catalogue.products
      .filter(({ device }) => device.quantity > 0)
      .map(({ device }) => getProductName(device.title))
      .join(' | ');
  }, []);

  const onClose = () => {
    onboardingTealiumService.onTakeAPhotoTipsModalAction(
      productNames,
      application.referenceNumber,
      imageUploadState.documentTypeName,
      imageUploadState.uploadType,
      'cancel',
    );

    imageUploadDispatch({
      type: ImageUploadActionType.TAKE_PHOTO_TIP,
      showModal: false,
    });
    dialogRef.current?.close();
  };

  const onNext = () => {
    onboardingTealiumService.onTakeAPhotoTipsModalAction(
      productNames,
      application.referenceNumber,
      imageUploadState.documentTypeName,
      imageUploadState.uploadType,
      'next',
    );
    imageUploadDispatch({
      type: ImageUploadActionType.SELECT_ID_TYPE,
      journey: ImageUploadJourney.CAMERA,
      showSelectIdType: true,
      documentTypeName: imageUploadState.documentTypeName,
      uploadType: imageUploadState.uploadType,
    });
    dialogRef.current?.close();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    if (event.key == 'Escape') {
      onboardingTealiumService.onTakeAPhotoTipsModalAction(
        productNames,
        application.referenceNumber,
        imageUploadState.documentTypeName,
        imageUploadState.uploadType,
        'cancel',
      );

      imageUploadDispatch({
        type: ImageUploadActionType.TAKE_PHOTO_TIP,
        showModal: false,
      });
      dialogRef.current?.close();
    }
    return;
  };

  useEffect(() => {
    onboardingTealiumService.onTakeAPhotoTipsModalLanding(
      productNames,
      application.referenceNumber,
      imageUploadState.documentTypeName,
      imageUploadState.uploadType,
    );
    dialogRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      imageUploadState.showModal &&
      imageUploadState.stepName === ImageUploadActionType.TAKE_PHOTO_TIP &&
      dialogRef
    ) {
      dialogRef.current?.showModal();
    }
  }, [imageUploadState.showModal]);

  const listItemClass =
    "relative pl-[30px] mx-6 before:content-[''] before:bg-tick-icon before:bg-cover before:bg-no-repeat before:w-5 before:h-5 before:absolute before:left-0 before:top-1/2 before:transform before:-translate-y-1/2";

  return (
    <dialog
      ref={dialogRef}
      className="flex flex-col items-center w-full gap-4 px-2 py-4 bg-white max-w-130 min-h-188 md:p-10 rounded-20 backdrop:opacity-95 backdrop:bg-black"
    >
      <WebcamChecker />
      <button className="absolute w-8 h-8 right-2 md:right-10" onClick={onClose}>
        <img src={CloseIconDark} alt="close" />
      </button>

      <header className="flex flex-col items-center gap-6 mt-10 md:mt-20">
        <h1 className="text-3xl md:text-4xl text-grey-750">Now let's take a photo of your ID</h1>
      </header>

      <div className="flex flex-col items-center gap-4">
        <img className="max-w-64 h-44" src={IdHelpDesktop} alt="ID help" />
        <h2 className="text-lg text-grey-750">Tips for taking a photo of your ID</h2>
        <ul className="flex flex-col gap-2">
          {tips.map((tip, index) => (
            <li key={index} className={listItemClass}>
              {tip}
            </li>
          ))}
        </ul>
      </div>

      <footer className="flex gap-2 md:gap-4">
        <button
          className="h-10 text-base font-medium text-center text-black bg-white border-2 border-black border-solid rounded-8 w-44"
          type="button"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          className="h-10 text-base font-medium text-center text-white bg-red-600 rounded-8 w-44"
          type="button"
          onClick={onNext}
        >
          Next
        </button>
      </footer>
    </dialog>
  );
}
